import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const COLORS = [
  "#8B5CF6", "#EC4899", "#1FAF5C", "#3B82F6", "#F59E0B",
  "#EF4444", "#06B6D4", "#84CC16", "#F97316", "#6366F1",
  "#14B8A6", "#A855F7", "#F43F5E", "#0EA5E9", "#D946EF",
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, e) => s + (e.value || 0), 0);
  return (
    <div className='bg-white rounded-xl border border-border-subtle shadow-lg px-4 py-3 max-w-[260px]'>
      <p className='text-body-xs font-semibold text-text-primary mb-1.5'>{label}</p>
      {payload
        .filter((e) => e.value > 0)
        .sort((a, b) => (b.value || 0) - (a.value || 0))
        .map((entry) => (
          <div key={entry.dataKey} className='flex items-center gap-2 text-body-xs'>
            <span className='w-2 h-2 rounded-full flex-shrink-0' style={{ backgroundColor: entry.color }} />
            <span className='text-text-secondary truncate max-w-[140px]'>{entry.name}</span>
            <span className='font-semibold text-text-primary ml-auto'>${entry.value?.toFixed(2)}</span>
          </div>
        ))}
      <div className='border-t border-border-subtle mt-1.5 pt-1.5 flex items-center justify-between text-body-xs'>
        <span className='text-text-secondary font-medium'>Total</span>
        <span className='font-bold text-text-primary'>${total.toFixed(2)}</span>
      </div>
    </div>
  );
};

// Hard cap on rendered series. The backend already folds low-cost groups
// into "other" (top_n param), but unbounded dimensions like caller can have
// 75+ values on older backends — rendering one stacked <Bar> per series
// froze the tab. This client-side cap is the defensive backstop.
const MAX_SERIES = 14;

const CostTrendsChart = ({ data, isLoading, error }) => {
  const { chartData, seriesKeys } = useMemo(() => {
    if (!data?.trends?.length) return { chartData: [], seriesKeys: [] };

    // Only consider entries with a valid YYYY-MM-DD date (records missing
    // call_ts can produce an "unknown" bucket, which broke date filling).
    const trends = data.trends.filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d.date || ""));
    if (!trends.length) return { chartData: [], seriesKeys: [] };

    const totals = {};
    trends.forEach((d) => {
      Object.keys(d).forEach((k) => {
        if (k !== "date" && k !== "total") totals[k] = (totals[k] || 0) + (d[k] || 0);
      });
    });

    const sorted = Object.keys(totals).sort((a, b) => totals[b] - totals[a]);
    let keep = sorted;
    let folded = null;
    if (sorted.length > MAX_SERIES) {
      // Keep "other" (server-side fold) last if present, fold the tail into it.
      keep = sorted.filter((k) => k !== "other").slice(0, MAX_SERIES - 1);
      folded = new Set(sorted.filter((k) => !keep.includes(k)));
    }

    const byDate = {};
    trends.forEach((d) => {
      let entry = d;
      if (folded) {
        entry = { date: d.date, total: d.total };
        let other = 0;
        Object.keys(d).forEach((k) => {
          if (k === "date" || k === "total") return;
          if (folded.has(k)) other += d[k] || 0;
          else entry[k] = d[k];
        });
        if (other > 0) entry.other = Math.round(other * 10000) / 10000;
      }
      byDate[d.date] = entry;
    });

    // Fill missing days using pure string/UTC math (local-timezone Date
    // arithmetic shifted days for UTC+ offsets).
    const dates = trends.map((d) => d.date).sort();
    const filled = [];
    const cursor = new Date(dates[0] + "T00:00:00Z");
    const end = new Date(dates[dates.length - 1] + "T00:00:00Z");
    while (cursor <= end) {
      const iso = cursor.toISOString().split("T")[0];
      filled.push(byDate[iso] || { date: iso, total: 0 });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }

    const seriesList = folded ? [...keep, "other"] : keep;
    return { chartData: filled, seriesKeys: seriesList };
  }, [data]);

  if (isLoading) {
    return (
      <div className='h-[350px] flex items-center justify-center'>
        <div className='flex flex-col items-center gap-2'>
          <div className='animate-spin rounded-full h-6 w-6 border-2 border-accent-purple border-t-transparent' />
          <span className='text-body-sm text-text-tertiary'>Loading trends...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='h-[350px] flex items-center justify-center'>
        <p className='text-body-sm text-severity-high'>Failed to load cost trends</p>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className='h-[350px] flex items-center justify-center'>
        <p className='text-body-sm text-text-tertiary'>No cost data available for this period</p>
      </div>
    );
  }

  return (
    <div className='h-[350px]'>
      <ResponsiveContainer width='100%' height='100%'>
        <BarChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray='3 3' stroke='rgba(10,16,48,0.06)' vertical={false} />
          <XAxis
            dataKey='date'
            tick={{ fontSize: 11, fill: "rgba(10,10,38,0.4)" }}
            axisLine={{ stroke: "rgba(10,16,48,0.08)" }}
            tickLine={false}
            tickFormatter={(v) => {
              const parts = v.split("-");
              return parts.length === 3 ? `${parts[1]}/${parts[2]}` : v;
            }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "rgba(10,10,38,0.4)" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${v}`}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(139,92,246,0.04)" }} />
          <Legend
            iconType='circle'
            iconSize={8}
            wrapperStyle={{ fontSize: "11px", paddingTop: "12px" }}
          />
          {seriesKeys.map((key, i) => (
            <Bar
              key={key}
              dataKey={key}
              stackId='stack'
              fill={key === "other" ? "#C7CAD4" : COLORS[i % COLORS.length]}
              name={key}
              radius={i === seriesKeys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CostTrendsChart;
